/**
 * Staff Onboarding - New member welcome flow
 *
 * Clean, step-by-step onboarding experience
 */

import { useState } from 'react'
import { Check, ChevronRight, User, Shield, Book, Key } from 'lucide-react'
import { cn } from '../../lib/utils'

interface OnboardingStep {
  id: string
  title: string
  description: string
  icon: typeof User
  completed: boolean
}

const INITIAL_STEPS: OnboardingStep[] = [
  { id: 'profile', title: 'Complete profile', description: 'Add your photo and bio', icon: User, completed: false },
  { id: 'security', title: 'Set up security', description: 'Enable 2FA and backup codes', icon: Shield, completed: false },
  { id: 'docs', title: 'Review documentation', description: 'Read the team guidelines', icon: Book, completed: false },
  { id: 'access', title: 'Request access', description: 'Get keys to your tools', icon: Key, completed: false },
]

export default function StaffOnboarding() {
  const [steps, setSteps] = useState(INITIAL_STEPS)
  const [currentStep, setCurrentStep] = useState(0)

  const completeStep = (index: number) => {
    setSteps(prev => prev.map((s, i) => i === index ? { ...s, completed: true } : s))
    if (index < steps.length - 1) {
      setCurrentStep(index + 1)
    }
  }

  const progress = (steps.filter(s => s.completed).length / steps.length) * 100

  return (
    <div className="max-w-2xl mx-auto py-8">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-3xl font-light text-white mb-2">Welcome to LUKHAS</h1>
        <p className="text-gray-500">Let's get you set up</p>
      </div>

      {/* Progress */}
      <div className="mb-12">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-500">Progress</span>
          <span className="text-sm text-white">{Math.round(progress)}%</span>
        </div>
        <div className="h-1 bg-white/10 rounded-full overflow-hidden">
          <div
            className="h-full bg-lambda-gold transition-all duration-500"
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Steps */}
      <div className="space-y-4">
        {steps.map((step, index) => {
          const Icon = step.icon
          const isActive = index === currentStep
          const isCompleted = step.completed

          return (
            <button
              key={step.id}
              onClick={() => !isCompleted && setCurrentStep(index)}
              className={cn(
                'w-full flex items-center gap-4 p-6 rounded-2xl text-left transition-all',
                isActive && !isCompleted && 'bg-white/5 ring-1 ring-white/10',
                isCompleted && 'bg-emerald-500/10',
                !isActive && !isCompleted && 'hover:bg-white/[0.02]'
              )}
            >
              {/* Icon */}
              <div className={cn(
                'w-12 h-12 rounded-xl flex items-center justify-center',
                isCompleted ? 'bg-emerald-500' : isActive ? 'bg-lambda-gold/20' : 'bg-white/5'
              )}>
                {isCompleted ? (
                  <Check className="w-5 h-5 text-white" />
                ) : (
                  <Icon className={cn('w-5 h-5', isActive ? 'text-lambda-gold' : 'text-gray-500')} />
                )}
              </div>

              {/* Content */}
              <div className="flex-1">
                <h3 className={cn(
                  'font-medium',
                  isCompleted ? 'text-emerald-400' : 'text-white'
                )}>
                  {step.title}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">{step.description}</p>
              </div>

              {/* Action */}
              {isActive && !isCompleted && (
                <button
                  onClick={(e) => { e.stopPropagation(); completeStep(index) }}
                  className="px-4 py-2 bg-lambda-gold text-black rounded-lg text-sm font-medium hover:bg-lambda-gold/90 transition-colors"
                >
                  Complete
                </button>
              )}
              {!isActive && !isCompleted && (
                <ChevronRight className="w-5 h-5 text-gray-600" />
              )}
            </button>
          )
        })}
      </div>

      {/* All Complete */}
      {progress === 100 && (
        <div className="mt-12 text-center p-8 rounded-2xl bg-lambda-gold/10 border border-lambda-gold/20">
          <h2 className="text-xl text-white mb-2">You're all set!</h2>
          <p className="text-gray-400 mb-6">Welcome to the team. You now have full access.</p>
          <button className="px-6 py-3 bg-lambda-gold text-black rounded-lg font-medium hover:bg-lambda-gold/90 transition-colors">
            Go to Dashboard
          </button>
        </div>
      )}
    </div>
  )
}
